// src/pages/materias/empresas/EmpresaDetallePage.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { notify } from "../../components/ui/notify";
import {
  Typography,
  Space,
  Button,
  Card,
  Modal,
  Spin,
  Empty,
  Tag,
  Row,
  Col,
  Input,
  Select,
  Switch,
} from "antd";
import { useDispatch } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { PATH } from "../../redux/utils";
import {
  actionEmpresaGetById,
  actionEmpresaUpdate,
  actionEmpresaDelete,
} from "../../redux/actions/empresas/empresas";
import { DataBadge } from "../../components/ui";
import { usePermisos } from "../../context/PermisosContext";
import { STATUS_PILL_HEX } from "./materias/laboral/tribunalEstatus.js";
import "../../assets/css/LaboralExpedienteDetallePage.css";
import "../../components/conciliacion/detalles/LaboralExpedienteDetallePage.css";
import "./materias/laboral/LaboralProcedimientoPage.css";
import "../../components/tribunal/TribunalPage.css";

dayjs.locale("es");

const { Text, Title } = Typography;

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
};

// Mismo patrón "label arriba / valor abajo" que usa la tarjeta "Datos del
// expediente" de Tribunal/Junta (laboral-exp-field-grid).
function Field({ label, value }) {
  return (
    <div className="laboral-exp-field">
      <span className="laboral-exp-field-label">{label}</span>
      <span className="laboral-exp-field-value">{value ?? "—"}</span>
    </div>
  );
}

const normNombre = (s) =>
  String(s ?? "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const emptyRazon = () => ({ nombre: "", tiene_corresponsal: false, corresponsales_ids: [] });

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: PATH,
  headers: { "Content-Type": "application/json" },
});

// ===================== Actividad Tab =====================
function ActividadTab({ actividad, loading }) {
  const fmtDt = (d) => {
    if (!d) return "—";
    const x = dayjs(d);
    return x.isValid() ? x.format("DD/MM/YYYY, HH:mm") : "—";
  };

  const getActionMeta = (action) => {
    const a = String(action || "").toUpperCase();
    if (a.includes("CREATE") || a.includes("CREAT") || a.includes("INSERT")) {
      return { label: "Registro", tagColor: "success", dot: "#52c41a" };
    }
    if (a.includes("UPDATE") || a.includes("UPDAT") || a.includes("EDIT") || a.includes("PATCH")) {
      return { label: "Actualización", tagColor: "processing", dot: "#1677ff" };
    }
    if (a.includes("DELETE") || a.includes("DELET")) {
      return { label: "Eliminación", tagColor: "error", dot: "#ff4d4f" };
    }
    return { label: toTitleCase(action || "Acción"), tagColor: "default", dot: "#8c8c8c" };
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      style={{ borderRadius: 12, marginTop: 4 }}
      bodyStyle={{ padding: "20px 24px" }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <Text strong style={{ fontSize: 17, color: "#111827" }}>
          Actividad de la empresa
        </Text>
        <div
          style={{
            background: "#f0f0f0",
            borderRadius: 20,
            padding: "2px 11px",
            fontSize: 13,
            fontWeight: 600,
            color: "#4b5563",
          }}
        >
          {actividad.length}
        </div>
      </div>

      {actividad.length === 0 ? (
        <Empty description="Sin actividad registrada para esta empresa" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {actividad.map((item, idx) => {
            const { label, tagColor, dot } = getActionMeta(item.action);
            const isLast = idx === actividad.length - 1;

            return (
              <div key={item.id_audit_log} style={{ display: "flex", gap: 14 }}>
                {/* Timeline dot + line */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    paddingTop: 17,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      background: dot,
                      boxShadow: `0 0 0 3px ${dot}22`,
                      flexShrink: 0,
                    }}
                  />
                  {!isLast && (
                    <div
                      style={{
                        width: 2,
                        flex: 1,
                        background: "#e5e7eb",
                        minHeight: 16,
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>

                {/* Card item */}
                <div style={{ flex: 1, paddingBottom: isLast ? 0 : 10 }}>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #f3f4f6",
                      borderRadius: 10,
                      padding: "14px 16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                    }}
                  >
                    {/* Top row: badge + message */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        marginBottom: 12,
                      }}
                    >
                      <Tag
                        color={tagColor}
                        style={{ margin: 0, fontWeight: 600, flexShrink: 0, fontSize: 12 }}
                      >
                        {label}
                      </Tag>
                      <Text style={{ fontSize: 14, lineHeight: 1.45, color: "#1f2937" }}>
                        {item.message || "—"}
                      </Text>
                    </div>

                    {/* Bottom row: date + user */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        gap: 8,
                      }}
                    >
                      <Text style={{ fontSize: 12, color: "#9ca3af" }}>
                        {fmtDt(item.datetime)}
                      </Text>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                          {item.user_name || "—"}
                        </div>
                        {item.user_email && (
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>
                            {item.user_email}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ===================== Main Page =====================
export default function EmpresaDetallePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { idEmpresa } = useParams();
  const location = useLocation();

  const { perm } = usePermisos() || { perm: () => true };
  const canEditar = perm("empresas", "editar");
  const canEliminar = perm("empresas", "eliminar");

  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("info");

  const [actividad, setActividad] = useState([]);
  const [loadingAct, setLoadingAct] = useState(false);
  const [loadedAct, setLoadedAct] = useState(false);

  // Edición inline de "Datos de la empresa" y "Razones sociales"
  const [editingDatos, setEditingDatos] = useState(false);
  const [editingForm, setEditingForm] = useState({});
  const [savingDatos, setSavingDatos] = useState(false);

  const [editingRazones, setEditingRazones] = useState(false);
  const [razonesForm, setRazonesForm] = useState([]);
  const [razonesErrors, setRazonesErrors] = useState([]);
  const [savingRazones, setSavingRazones] = useState(false);
  const [corresponsalesCatalog, setCorresponsalesCatalog] = useState([]);

  const loadDetalle = useCallback(async () => {
    try {
      setLoadingDetalle(true);
      const data = await dispatch(actionEmpresaGetById(idEmpresa));
      setDetalle(data || null);
    } catch {
      notify.error({
        message: "Error al cargar",
        description: "No se pudo obtener el detalle de la empresa.",
      });
      setDetalle(null);
    } finally {
      setLoadingDetalle(false);
    }
  }, [dispatch, idEmpresa]);

  // Load empresa
  useEffect(() => {
    loadDetalle();
  }, [loadDetalle]);

  useEffect(() => {
    api
      .get("empresas/corresponsales-catalog", { headers: authHeader() })
      .then(({ data }) => setCorresponsalesCatalog(data || []))
      .catch(() => {});
  }, []);

  const corresponsalesOptions = useMemo(
    () => corresponsalesCatalog.map((c) => ({ label: c.nombre, value: c.id_corresponsal })),
    [corresponsalesCatalog]
  );

  const startEditDatos = () => {
    setEditingForm({
      nombre: detalle?.nombre || "",
      correo: detalle?.correo || "",
      celular: detalle?.celular || "",
    });
    setEditingDatos(true);
  };

  const handleSaveDatos = async () => {
    const nombre = (editingForm.nombre || "").trim();
    if (!nombre) {
      notify.error({ message: "Falta el nombre de la empresa", placement: "bottomRight" });
      return;
    }
    setSavingDatos(true);
    try {
      const idToUpdate = detalle?.id_empresa ?? detalle?.id;
      const payload = {
        nombre,
        correo: (editingForm.correo || "").trim() || null,
        celular: (editingForm.celular || "").replace(/\D/g, "").slice(0, 15) || null,
      };
      await dispatch(actionEmpresaUpdate(idToUpdate, payload, () => {}));
      await loadDetalle();
      setEditingDatos(false);
      notify.success({
        message: "Empresa actualizada",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingDatos(false);
    }
  };

  const startEditRazones = () => {
    const razonesList = Array.isArray(detalle?.razones_sociales) ? detalle.razones_sociales : [];
    const mapped = razonesList.length
      ? razonesList.map((r) => {
          const corresponsales = Array.isArray(r?.corresponsales) ? r.corresponsales : [];
          const ids = corresponsales.map((c) => c.id || c.id_corresponsal).filter(Boolean);
          return { nombre: r?.nombre || "", tiene_corresponsal: ids.length > 0, corresponsales_ids: ids };
        })
      : [emptyRazon()];
    setRazonesForm(mapped);
    setRazonesErrors(mapped.map(() => ""));
    setEditingRazones(true);
  };

  const setRazonField = (idx, patch) => {
    setRazonesForm((list) => {
      const next = list.slice();
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const addRazon = () => setRazonesForm((list) => [...list, emptyRazon()]);
  const removeRazon = (idx) => setRazonesForm((list) => list.filter((_, i) => i !== idx));

  const handleSaveRazones = async () => {
    const errs = razonesForm.map(() => "");
    const seen = new Map();
    let hasErrors = false;

    razonesForm.forEach((r, idx) => {
      const nombre = (r.nombre || "").trim();
      if (!nombre) {
        errs[idx] = "Requerido";
        hasErrors = true;
        return;
      }
      const key = normNombre(nombre);
      if (seen.has(key)) {
        errs[seen.get(key)] = "Razón social duplicada";
        errs[idx] = "Razón social duplicada";
        hasErrors = true;
      } else {
        seen.set(key, idx);
      }
    });

    setRazonesErrors(errs);
    if (hasErrors) {
      notify.error({ message: "Revisa las razones sociales marcadas en rojo", placement: "bottomRight" });
      return;
    }

    const rsClean = razonesForm
      .map((r) => ({
        nombre: (r.nombre || "").trim(),
        corresponsales_ids: r.tiene_corresponsal
          ? Array.isArray(r.corresponsales_ids) ? r.corresponsales_ids : []
          : [],
      }))
      .filter((r) => !!r.nombre);

    setSavingRazones(true);
    try {
      const idToUpdate = detalle?.id_empresa ?? detalle?.id;
      await dispatch(actionEmpresaUpdate(idToUpdate, { razones_sociales: rsClean }, () => {}));
      await loadDetalle();
      setEditingRazones(false);
      notify.success({
        message: "Razones sociales actualizadas",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingRazones(false);
    }
  };

  // Load actividad (GET /empresas/{id}/historial)
  const loadActividad = useCallback(async () => {
    if (loadedAct) return;
    try {
      setLoadingAct(true);
      const { data } = await api.get(`empresas/${idEmpresa}/historial`, {
        headers: authHeader(),
        params: { limit: 200 },
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setActividad(items);
    } catch (err) {
      console.error(err);
      notify.error({ message: "Error", description: "No se pudo cargar la actividad." });
    } finally {
      setLoadingAct(false);
      setLoadedAct(true);
    }
  }, [idEmpresa, loadedAct]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "actividad") loadActividad();
  };

  const handleDeleteEmpresa = () => {
    const idToDelete = detalle?.id_empresa ?? detalle?.id;
    if (!idToDelete) {
      notify.error({ message: "No se puede eliminar", description: "No se encontró el ID de la empresa." });
      return;
    }
    Modal.confirm({
      title: "Eliminar empresa",
      content: "Esta acción elimina la empresa y su información relacionada.",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      okButtonProps: { loading: deleting, disabled: deleting },
      cancelButtonProps: { disabled: deleting },
      onOk: async () => {
        try {
          setDeleting(true);
          await dispatch(
            actionEmpresaDelete(idToDelete, () => {
              notify.success({ message: "Empresa eliminada", description: "Se eliminó correctamente." });
              navigate("/empresas");
            })
          );
        } catch (error) {
          const msg = error?.response?.data?.detail || error?.message || "No se pudo eliminar la empresa.";
          notify.error({ message: "Error al eliminar", description: msg });
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loadingDetalle || !detalle) {
    return (
      <div className="lxp-root">
        <div className="lxp-wrap">
          <Card className="laboral-exp-summary-card p-2" style={{ marginTop: 16 }}>
            {loadingDetalle ? (
              <div style={{ padding: 40, textAlign: "center" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Text type="secondary">Sin datos de empresa.</Text>
            )}
          </Card>
        </div>
      </div>
    );
  }

  const empresaNombre = detalle?.nombre || "empresa";
  const isActive = Number(detalle?.active ?? 1) === 1;
  const estatusPill = isActive
    ? { label: "ACTIVO", kind: "primary" }
    : { label: "INACTIVO", kind: "gray" };
  const razonesList = Array.isArray(detalle?.razones_sociales) ? detalle.razones_sociales : [];

  const TABS = [
    { key: "info", label: "Info", icon: <i className="fa-solid fa-circle-info" /> },
    { key: "actividad", label: "Actividad", icon: <i className="fa-solid fa-bolt" /> },
  ];

  return (
    <div className="lxp-root">
      <div className="lxp-wrap">
        {/* SUMMARY CARD — mismas clases laboral-exp-* que el header de Tribunal/Junta */}
        <div className="laboral-exp-main" style={{ background: "transparent", padding: 0, minHeight: "auto" }}>
          <Card className="laboral-exp-summary-card p-2">
            <Row align="middle" gutter={[16, 16]} justify="space-between" wrap>
              <Col flex="auto">
                <Space direction="vertical" size={8}>
                  <div className="laboral-exp-summary-title">
                    <Space align="center" wrap>
                      <div className="laboral-exp-icon-badge">
                        <i className="fa-solid fa-building" />
                      </div>
                      <Title
                        level={4}
                        style={{ margin: 0, fontSize: 28, fontWeight: 650, letterSpacing: "-0.01em", lineHeight: 1.2, color: "#0f172a" }}
                      >
                        {toTitleCase(empresaNombre).toUpperCase()}
                      </Title>
                      <DataBadge
                        color={STATUS_PILL_HEX[estatusPill.kind] || estatusPill.kind}
                        className="d-inline-flex align-items-center gap-1 status-pill-badge"
                      >
                        {estatusPill.label}
                      </DataBadge>
                    </Space>
                  </div>

                  <Space size="large" wrap className="laboral-exp-summary-meta">
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-calendar-days" />
                      <Text>Creado: {fmtDateLong(detalle.created_at)}</Text>
                    </Space>
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-calendar-check" />
                      <Text>Actualizado: {fmtDateLong(detalle.updated_at)}</Text>
                    </Space>
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-address-card" />
                      <Text>Creado por: {toTitleCase(detalle.nombre_creado_por)}</Text>
                    </Space>
                  </Space>
                </Space>
              </Col>

              <Col flex="none">
                <Space>
                  {canEliminar && (
                  <Button
                    danger
                    type="primary"
                    icon={<i className="fa-solid fa-trash" />}
                    style={{ borderRadius: 8 }}
                    onClick={handleDeleteEmpresa}
                    loading={deleting}
                    disabled={deleting}
                  >
                    Eliminar
                  </Button>
                  )}
                </Space>
              </Col>
            </Row>

            {/* TABS BAR */}
            <div className="laboral-exp-header-tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`laboral-exp-tab-btn ${activeTab === t.key ? "laboral-exp-tab-btn-active" : ""}`}
                  onClick={() => handleTabChange(t.key)}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* TAB: INFO */}
        {activeTab === "info" && (
          <>
            <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ marginTop: 18 }}>
              <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="laboral-exp-icon-badge">
                    <i className="fa-solid fa-id-card" />
                  </div>
                  <h3 className="laboral-exp-card-header-title">Datos de la empresa</h3>
                </div>

                {canEditar && !editingDatos && (
                  <Button onClick={startEditDatos} className="laboral-exp-btn-clean">
                    Editar
                  </Button>
                )}
              </div>

              {editingDatos ? (
                <div className="laboral-exp-edit-form">
                  <div className="laboral-exp-edit-grid">
                    <div className="laboral-exp-edit-field">
                      <span className="laboral-exp-edit-label">Nombre comercial</span>
                      <Input
                        value={editingForm.nombre}
                        onChange={(e) => setEditingForm((f) => ({ ...f, nombre: e.target.value }))}
                      />
                    </div>
                    <div className="laboral-exp-edit-field">
                      <span className="laboral-exp-edit-label">Correo</span>
                      <Input
                        value={editingForm.correo}
                        onChange={(e) => setEditingForm((f) => ({ ...f, correo: e.target.value }))}
                      />
                    </div>
                    <div className="laboral-exp-edit-field">
                      <span className="laboral-exp-edit-label">Celular</span>
                      <Input
                        value={editingForm.celular}
                        onChange={(e) =>
                          setEditingForm((f) => ({ ...f, celular: e.target.value.replace(/\D/g, "").slice(0, 15) }))
                        }
                      />
                    </div>
                  </div>

                  <div className="laboral-exp-edit-actions">
                    <Button onClick={() => setEditingDatos(false)} className="laboral-exp-btn-clean">
                      Cancelar
                    </Button>
                    <Button
                      type="primary"
                      loading={savingDatos}
                      onClick={handleSaveDatos}
                      className="laboral-exp-btn-save"
                      icon={<i className="fa-solid fa-floppy-disk" />}
                    >
                      Guardar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="laboral-exp-field-grid">
                  <Field label="Nombre comercial" value={toTitleCase(detalle?.nombre)} />
                  <Field label="Estatus" value={isActive ? "Activo" : "Inactivo"} />
                  <Field label="Correo" value={detalle?.correo || "—"} />
                  <Field label="Celular" value={detalle?.celular || "—"} />
                </div>
              )}
            </Card>

            <Card className="laboral-exp-detail-card p-2" style={{ marginTop: 18 }}>
              <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="laboral-exp-icon-badge">
                    <i className="fa-solid fa-sitemap" />
                  </div>
                  <h3 className="laboral-exp-card-header-title">
                    Razones sociales {!editingRazones && `(${razonesList.length})`}
                  </h3>
                </div>

                {canEditar && !editingRazones && (
                  <Button onClick={startEditRazones} className="laboral-exp-btn-clean">
                    Editar
                  </Button>
                )}
              </div>

              {editingRazones ? (
                <div className="laboral-exp-edit-form">
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {razonesForm.map((r, idx) => (
                      <div
                        key={idx}
                        style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, background: "#f8fafc" }}
                      >
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <div className="laboral-exp-edit-field" style={{ flex: 1 }}>
                            <span className="laboral-exp-edit-label">Razón social</span>
                            <Input
                              status={razonesErrors[idx] ? "error" : ""}
                              value={r.nombre}
                              onChange={(e) => setRazonField(idx, { nombre: e.target.value })}
                            />
                            {razonesErrors[idx] && <div className="oa-error">{razonesErrors[idx]}</div>}
                          </div>
                          <Button
                            danger
                            onClick={() => removeRazon(idx)}
                            disabled={razonesForm.length <= 1}
                            style={{ marginTop: 20 }}
                          >
                            Quitar
                          </Button>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                          <Switch
                            size="small"
                            checked={r.tiene_corresponsal}
                            onChange={(checked) => setRazonField(idx, { tiene_corresponsal: checked })}
                          />
                          <span style={{ fontSize: 12.5, color: "#64748b" }}>Tiene corresponsal</span>
                        </div>

                        {r.tiene_corresponsal && (
                          <div className="laboral-exp-edit-field" style={{ marginTop: 10 }}>
                            <span className="laboral-exp-edit-label">Corresponsales</span>
                            <Select
                              mode="multiple"
                              value={r.corresponsales_ids}
                              onChange={(ids) => setRazonField(idx, { corresponsales_ids: ids })}
                              options={corresponsalesOptions}
                              style={{ width: "100%" }}
                              placeholder="— Seleccionar despacho —"
                              optionFilterProp="label"
                              showSearch
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    <Button onClick={addRazon} className="laboral-exp-btn-clean" icon={<i className="fa-solid fa-plus" />}>
                      Agregar razón social
                    </Button>
                  </div>

                  <div className="laboral-exp-edit-actions">
                    <Button onClick={() => setEditingRazones(false)} className="laboral-exp-btn-clean">
                      Cancelar
                    </Button>
                    <Button
                      type="primary"
                      loading={savingRazones}
                      onClick={handleSaveRazones}
                      className="laboral-exp-btn-save"
                      icon={<i className="fa-solid fa-floppy-disk" />}
                    >
                      Guardar
                    </Button>
                  </div>
                </div>
              ) : razonesList.length === 0 ? (
                <Empty description="Esta empresa no tiene razones sociales registradas." />
              ) : (
                <div className="laboral-exp-rs-list">
                  {razonesList.map((rs, idx) => {
                    const corresponsales = Array.isArray(rs.corresponsales) ? rs.corresponsales : [];
                    return (
                      <div className="laboral-exp-rs-item" key={rs.id || idx}>
                        <div className="laboral-exp-rs-ordinal">{idx + 1}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="laboral-exp-rs-name">{toTitleCase(rs.nombre)}</div>
                          {corresponsales.length > 0 && (
                            <div className="laboral-exp-rs-meta">
                              {corresponsales.map((c) => (
                                <Tag key={c.id} color="geekblue" style={{ fontSize: 12, fontWeight: 500, borderRadius: 6 }}>
                                  {c.nombre}
                                </Tag>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </>
        )}

        {/* TAB: ACTIVIDAD */}
        {activeTab === "actividad" && (
          <div style={{ marginTop: 18 }}>
            <ActividadTab actividad={actividad} loading={loadingAct} />
          </div>
        )}
      </div>
    </div>
  );
}
